import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import { FormikProvider, useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import * as Yup from "yup";
import axiosInstance from "../../../utils/axiosInstance";
import { modalStyle } from "../../../utils/styles";

export default function UserModal({ open, onClose, onSubmit, user }) {
  const [roles, setRoles] = useState([]);

  const formik = useFormik({
    initialValues: {
      full_name: user ? user.full_name : "",
      email: user ? user.email : "",
      password: "",
      phone_number: user ? user.phone_number : "",
      role: user ? user.role : -1,
    },
    validationSchema: Yup.object({
      full_name: Yup.string().required("Full name is required"),
      email: Yup.string()
        .email("Invalid email format")
        .required("Email is required"),
      password: Yup.string().min(8, "Password must be at least 8 characters"),
      phone_number: user
        ? Yup.string().notRequired()
        : Yup.string().required("Phone number is required"),
      role: Yup.string().required("Role is required"),
    }),
    onSubmit: (values) => {
      const data = {};
      data.full_name = values.full_name;
      data.email = values.email;
      data.phone_number = values.phone_number;
      data.role = values.role;
      if (values.password) {
        data.password = values.password;
      }

      // close the modal and reset the form
      formik.resetForm();
      onClose();
      onSubmit(data, user?.id);
      formik.resetForm();
      onClose();
    },
  });

  const fetchRoles = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/roles`,
      );
      if (response.status === 200) {
        setRoles(response.data);
      }
    } catch (error) {}
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography id="modal-modal-title" variant="h6" component="h2">
          {user ? "Update User" : "Add New User"}
        </Typography>

        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              placeholder="Full Name"
              variant="outlined"
              name="full_name"
              value={formik.values.full_name}
              onChange={formik.handleChange}
              error={
                formik.touched.full_name && Boolean(formik.errors.full_name)
              }
              helperText={formik.touched.full_name && formik.errors.full_name}
            />

            <TextField
              fullWidth
              margin="normal"
              placeholder="Email"
              variant="outlined"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />

            {/* Hide the password field when editing */}
            {!user && (
              <TextField
                fullWidth
                margin="normal"
                placeholder="Password"
                variant="outlined"
                type="password"
                name="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={formik.touched.password && formik.errors.password}
              />
            )}

            <TextField
              fullWidth
              margin="normal"
              placeholder="Phone Number"
              variant="outlined"
              name="phone_number"
              value={formik.values.phone_number}
              onChange={formik.handleChange}
              error={
                formik.touched.phone_number &&
                Boolean(formik.errors.phone_number)
              }
              helperText={
                formik.touched.phone_number && formik.errors.phone_number
              }
            />

            <FormControl fullWidth margin="normal">
              <Select
                id="role"
                name="role"
                value={formik.values.role}
                onChange={formik.handleChange}
                error={formik.touched.role && Boolean(formik.errors.role)}
              >
                <MenuItem value={-1}>--Select role--</MenuItem>

                {roles &&
                  roles.map((role, index) => (
                    <MenuItem key={index} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              sx={{ mt: 3 }}
            >
              {user ? "Update User" : "Add User"}
            </Button>
          </form>
        </FormikProvider>
      </Box>
    </Modal>
  );
}
